/**
 * Icon — the canonical icon component for Cheffo.
 *
 * Mirrors design/icons.jsx (the 66-icon canonical set externalised by
 * Claude Design). Replaces the lucide-react dependency + 22+ scattered
 * inline SVGs that the app used to ship with — see #108 for the migration.
 *
 * Usage:
 *   <Icon name="bookmark" />                  // 18×18 default
 *   <Icon name="bell" size={22} />
 *   <Icon name="bookmark-filled" />           // separately-named filled variant
 *   <Icon name="loading" data-icon-spin />    // spinning loader
 *
 * Conventions (do not violate):
 *   - viewBox: 0 0 24 24 for every icon.
 *   - Stroke: 1.75, round caps, round joins, currentColor, fill none.
 *     Filled variants are SEPARATELY NAMED icons (bookmark vs bookmark-
 *     filled), not props.
 *   - Single colour. currentColor only. The one multicolour exception
 *     (auth-provider) uses design tokens directly. Cheffo's logo is a TEXT
 *     wordmark, never a symbol — there is deliberately no brand glyph here.
 *   - aria-hidden="true" by default. Icon-only buttons carry aria-label
 *     on the wrapping element, not the icon.
 *   - Size scale: 14, 16, 18, 20, 22, 24, 28, 32. Default 18.
 */

import type { SVGProps } from 'react';

export type IconName =
  // navigation
  | 'home' | 'chef' | 'book' | 'calendar' | 'cart' | 'pantry'
  | 'search' | 'bell' | 'user' | 'settings' | 'logout'
  | 'menu' | 'x'
  | 'arrow-left' | 'arrow-right' | 'arrow-up' | 'arrow-down'
  | 'chevron-left' | 'chevron-right' | 'chevron-up' | 'chevron-down'
  // actions
  | 'plus' | 'minus' | 'check' | 'edit' | 'trash' | 'copy' | 'share'
  | 'download' | 'upload' | 'refresh' | 'play' | 'pause' | 'cast'
  | 'more-horizontal' | 'more-vertical'
  | 'external-link' | 'link' | 'filter' | 'sort'
  | 'archive' | 'camera' | 'circle' | 'clipboard-check' | 'file-text' | 'globe'
  | 'grip-vertical' | 'grid' | 'list' | 'image' | 'languages' | 'layers' | 'paperclip'
  // content
  | 'bookmark' | 'bookmark-filled'
  | 'heart' | 'heart-filled'
  | 'star' | 'star-filled'
  | 'timer' | 'flame' | 'scale' | 'fork-knife'
  | 'leaf' | 'sprout' | 'wheat' | 'wheat-off' | 'milk' | 'milk-off' | 'salt'
  | 'egg' | 'fish' | 'meat' | 'sparkles'
  | 'users' | 'zap'
  // status
  | 'info' | 'warn' | 'error' | 'success' | 'loading'
  // theme
  | 'sun' | 'moon'
  // brand (multicolour, opt-in)
  | 'auth-provider';

export interface IconProps extends Omit<SVGProps<SVGSVGElement>, 'name'> {
  name: IconName;
  size?: number;
  /** Override the canonical stroke width (1.75). Almost never needed. */
  strokeWidth?: number;
}

type IconDef = {
  body: React.ReactNode;
  filled?: boolean;
  multi?: boolean;
};

const ICONS: Record<IconName, IconDef> = {
  // ── navigation ─────────────────────────────────────────────
  'home': { body: <><path d="M3 11l9-8 9 8"/><path d="M5 10v10a1 1 0 0 0 1 1h4v-6h4v6h4a1 1 0 0 0 1-1V10"/></> },
  'chef': { body: <><path d="M7 14a4 4 0 1 1 1.5-7.7 4 4 0 0 1 7 0A4 4 0 1 1 17 14"/><path d="M7 14h10v5a2 2 0 0 1-2 2H9a2 2 0 0 1-2-2z"/></> },
  'book': { body: <><path d="M4 5a2 2 0 0 1 2-2h13v16H6a2 2 0 0 0-2 2z"/><path d="M4 19a2 2 0 0 1 2-2h13"/></> },
  'calendar': { body: <><rect x="3" y="5" width="18" height="16" rx="2"/><path d="M3 10h18M8 3v4M16 3v4"/></> },
  'cart': { body: <><path d="M3 4h2l2.5 12.5a2 2 0 0 0 2 1.5h7.5a2 2 0 0 0 2-1.5L21 8H6"/><circle cx="10" cy="20" r="1.2"/><circle cx="17" cy="20" r="1.2"/></> },
  'pantry': { body: <><rect x="4" y="3" width="16" height="18" rx="2"/><path d="M4 9h16M4 15h16"/><path d="M9 6v0M9 12v0M9 18v0"/></> },
  'search': { body: <><circle cx="11" cy="11" r="7"/><path d="m20 20-3.5-3.5"/></> },
  'bell': { body: <><path d="M6 8a6 6 0 1 1 12 0c0 5 2 6 2 6H4s2-1 2-6z"/><path d="M10 19a2 2 0 0 0 4 0"/></> },
  'user': { body: <><circle cx="12" cy="8" r="4"/><path d="M4 21a8 8 0 0 1 16 0"/></> },
  'settings': { body: <><path d="M10.4 5.5 L10.4 3 L13.6 3 L13.6 5.5 A6.5 6.5 0 0 1 16.83 7.36 L18.99 6.11 L20.59 8.89 L18.43 10.14 A6.5 6.5 0 0 1 18.43 13.86 L20.59 15.11 L18.99 17.89 L16.83 14.64 A6.5 6.5 0 0 1 13.6 18.5 L13.6 21 L10.4 21 L10.4 18.5 A6.5 6.5 0 0 1 7.17 16.64 L5.01 17.89 L3.41 15.11 L5.57 13.86 A6.5 6.5 0 0 1 5.57 10.14 L3.41 8.89 L5.01 6.11 L7.17 7.36 A6.5 6.5 0 0 1 10.4 5.5 Z"/><circle cx="12" cy="12" r="3"/></> },
  'logout': { body: <><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><path d="m16 17 5-5-5-5"/><path d="M21 12H9"/></> },
  'menu': { body: <path d="M4 7h16M4 12h16M4 17h16"/> },
  'grid': { body: <><rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/><rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></> },
  'list': { body: <><path d="M8 6h13M8 12h13M8 18h13"/><path d="M3.5 6h.01M3.5 12h.01M3.5 18h.01"/></> },
  'x': { body: <path d="m6 6 12 12M18 6 6 18"/> },
  'arrow-left': { body: <path d="M19 12H5M11 18l-6-6 6-6"/> },
  'arrow-right': { body: <path d="M5 12h14M13 6l6 6-6 6"/> },
  'chevron-left': { body: <path d="m15 6-6 6 6 6"/> },
  'chevron-right': { body: <path d="m9 6 6 6-6 6"/> },
  'chevron-up': { body: <path d="m6 15 6-6 6 6"/> },
  'chevron-down': { body: <path d="m6 9 6 6 6-6"/> },

  // ── actions ────────────────────────────────────────────────
  'plus': { body: <path d="M12 5v14M5 12h14"/> },
  'minus': { body: <path d="M5 12h14"/> },
  'check': { body: <path d="m5 12 5 5L20 7"/> },
  'edit': { body: <><path d="M4 20h4l10-10-4-4L4 16z"/><path d="m14 6 4 4"/></> },
  'trash': { body: <><path d="M4 7h16"/><path d="M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/><path d="M6 7v13a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V7"/><path d="M10 11v6M14 11v6"/></> },
  'copy': { body: <><rect x="8" y="8" width="12" height="12" rx="2"/><path d="M16 8V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h2"/></> },
  'share': { body: <><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><path d="m8.6 10.5 6.8-4M8.6 13.5l6.8 4"/></> },
  'download': { body: <><path d="M12 4v12"/><path d="m7 11 5 5 5-5"/><path d="M4 20h16"/></> },
  'upload': { body: <><path d="M12 20V8"/><path d="m7 13 5-5 5 5"/><path d="M4 4h16"/></> },
  'refresh': { body: <><path d="M21 4v6h-6"/><path d="M3 20v-6h6"/><path d="M20 10a8 8 0 0 0-14-3l-3 3"/><path d="M4 14a8 8 0 0 0 14 3l3-3"/></> },
  'play': { body: <path d="M7 5v14l12-7z" fill="currentColor" stroke="none"/> },
  // Google Cast glyph: screen outline open at the bottom-left, with the
  // two broadcast arcs and their origin dot sitting in the gap.
  'cast': { body: <><path d="M3 9V7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2h-6"/><path d="M3 13a8 8 0 0 1 8 8"/><path d="M3 17a4 4 0 0 1 4 4"/><path d="M3 21h.01"/></> },
  'pause': { body: <path d="M9 5v14M15 5v14"/> },
  'more-horizontal': { body: <><circle cx="6" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="18" cy="12" r="1.2" fill="currentColor"/></> },
  'more-vertical': { body: <><circle cx="12" cy="6" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="18" r="1.2" fill="currentColor"/></> },
  'external-link': { body: <><path d="M14 4h6v6"/><path d="M20 4l-8 8"/><path d="M9 4H6a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3"/></> },
  'link': { body: <><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1"/><path d="M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1"/></> },
  'filter': { body: <path d="M3 5h18l-7 8v5l-4 2v-7z"/> },
  'sort': { body: <><path d="M7 4v16M3 8l4-4 4 4"/><path d="M17 20V4M13 16l4 4 4-4"/></> },
  'arrow-up': { body: <path d="M12 19V5M6 11l6-6 6 6"/> },
  'arrow-down': { body: <path d="M12 5v14M6 13l6 6 6-6"/> },
  'archive': { body: <><rect x="3" y="4" width="18" height="4" rx="1"/><path d="M5 8v11a1 1 0 0 0 1 1h12a1 1 0 0 0 1-1V8"/><path d="M10 12h4"/></> },
  'camera': { body: <><path d="M3 9a2 2 0 0 1 2-2h2l1.2-2h5.6L17 7h2a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><circle cx="12" cy="13" r="3.5"/></> },
  'circle': { body: <circle cx="12" cy="12" r="8"/> },
  'clipboard-check': { body: <><path d="M9 4H7a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V6a2 2 0 0 0-2-2h-2"/><rect x="9" y="2" width="6" height="4" rx="1"/><path d="m9 14 2 2 4-4"/></> },
  'file-text': { body: <><path d="M6 3h8l4 4v14a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z"/><path d="M14 3v4h4"/><path d="M8 13h8M8 17h6"/></> },
  'globe': { body: <><circle cx="12" cy="12" r="9"/><path d="M3 12h18"/><path d="M12 3a14 14 0 0 1 0 18 14 14 0 0 1 0-18z"/></> },
  'grip-vertical': { body: <><circle cx="9" cy="6" r="1.2" fill="currentColor"/><circle cx="9" cy="12" r="1.2" fill="currentColor"/><circle cx="9" cy="18" r="1.2" fill="currentColor"/><circle cx="15" cy="6" r="1.2" fill="currentColor"/><circle cx="15" cy="12" r="1.2" fill="currentColor"/><circle cx="15" cy="18" r="1.2" fill="currentColor"/></> },
  'image': { body: <><rect x="3" y="4" width="18" height="16" rx="2"/><circle cx="8.5" cy="9.5" r="1.5"/><path d="m4 18 5-5 4 4 3-3 4 4"/></> },
  'languages': { body: <><path d="M3 5h8"/><path d="M7 3v2c0 4-2 7-5 8"/><path d="M5 9c0 3 2.5 5 5 6"/><path d="m13 20 4-9 4 9"/><path d="M14.5 17h5"/></> },
  'layers': { body: <><path d="m12 3 9 5-9 5-9-5z"/><path d="m3 13 9 5 9-5"/></> },
  'paperclip': { body: <path d="M20 11l-8 8a5 5 0 0 1-7-7l8.5-8.5a3.5 3.5 0 0 1 5 5L10 17a2 2 0 0 1-3-3l7-7"/> },

  // ── content (recipe domain) ────────────────────────────────
  'bookmark': { body: <path d="M6 4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17l-6-4-6 4z"/> },
  'bookmark-filled': { filled: true, body: <path d="M6 4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17l-6-4-6 4z"/> },
  'heart': { body: <path d="M12 20.5s-7.5-4.7-9.5-9.5a5.3 5.3 0 0 1 9.5-3.3 5.3 5.3 0 0 1 9.5 3.3c-2 4.8-9.5 9.5-9.5 9.5z"/> },
  'heart-filled': { filled: true, body: <path d="M12 20.5s-7.5-4.7-9.5-9.5a5.3 5.3 0 0 1 9.5-3.3 5.3 5.3 0 0 1 9.5 3.3c-2 4.8-9.5 9.5-9.5 9.5z"/> },
  'star': { body: <path d="m12 3 2.7 5.7 6.3.9-4.5 4.3 1 6.1L12 17.5 6.5 20l1-6.1L3 9.6l6.3-.9z"/> },
  'star-filled': { filled: true, body: <path d="m12 3 2.7 5.7 6.3.9-4.5 4.3 1 6.1L12 17.5 6.5 20l1-6.1L3 9.6l6.3-.9z"/> },
  'timer': { body: <><circle cx="12" cy="14" r="7"/><path d="M12 14V10"/><path d="M10 3h4"/><path d="M19 6l1.5-1.5"/></> },
  'flame': { body: <path d="M12 3c2 3 5 6 5 10a5 5 0 0 1-10 0c0-3 1-4 2-6 1 2 2 3 3 3 0-2-1-4 0-7z"/> },
  'scale': { body: <><path d="M5 5h14"/><path d="M12 5v15"/><path d="M8 20h8"/><path d="M6 7 3 14h6z"/><path d="M18 7l-3 7h6z"/></> },
  'fork-knife': { body: <><path d="M7 3v6a2 2 0 0 0 2 2v10"/><path d="M5 3v6M9 3v6"/><path d="M17 3c-2 0-3 2-3 5s1 4 3 5v8"/></> },
  'leaf': { body: <><path d="M5 19c0-8 6-14 14-14 0 8-6 14-14 14z"/><path d="M5 19c4-4 7-7 11-11"/></> },
  'sprout': { body: <><path d="M12 20v-7"/><path d="M12 13c0-3-2-5-5-5-1 0-2 .3-2 .3 0 3 2 5 5 5 .8 0 1.4-.1 2-.3z"/><path d="M12 11c0-3 2-5 5-5 1 0 2 .3 2 .3 0 3-2 5-5 5-.8 0-1.4-.1-2-.3z"/><path d="M8 20h8"/></> },
  'wheat': { body: <><path d="M12 3v18"/><path d="M9 6c0 1.5 1 2.5 3 3 0-1.5-1-2.5-3-3z"/><path d="M15 6c0 1.5-1 2.5-3 3 0-1.5 1-2.5 3-3z"/><path d="M9 11c0 1.5 1 2.5 3 3 0-1.5-1-2.5-3-3z"/><path d="M15 11c0 1.5-1 2.5-3 3 0-1.5 1-2.5 3-3z"/><path d="M9 16c0 1.5 1 2.5 3 3 0-1.5-1-2.5-3-3z"/><path d="M15 16c0 1.5-1 2.5-3 3 0-1.5 1-2.5 3-3z"/></> },
  'wheat-off': { body: <><path d="M12 3v6"/><path d="M12 15v6"/><path d="M9 6c0 1.5 1 2.5 3 3"/><path d="M15 6c0 1.5-1 2.5-3 3"/><path d="M9 16c0 1.5 1 2.5 3 3 0-1.5-1-2.5-3-3z"/><path d="M15 16c0 1.5-1 2.5-3 3 0-1.5 1-2.5 3-3z"/><path d="m3 3 18 18"/></> },
  'milk': { body: <><path d="M9 3h6v3l2 3v10a1 1 0 0 1-1 1H8a1 1 0 0 1-1-1V9l2-3z"/><path d="M9 6h6"/><path d="M9 13h6"/></> },
  'milk-off': { body: <><path d="M9 3h6v3l1 1.5"/><path d="M7 11v8a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1v-6"/><path d="m3 3 18 18"/></> },
  'salt': { body: <><path d="M9 4h6v3l1 2v11a1 1 0 0 1-1 1H9a1 1 0 0 1-1-1V9l1-2z"/><path d="M9 4l-0.5-1h7L15 4"/><circle cx="11" cy="13" r=".5" fill="currentColor"/><circle cx="13" cy="11" r=".5" fill="currentColor"/><circle cx="14" cy="15" r=".5" fill="currentColor"/><circle cx="11" cy="17" r=".5" fill="currentColor"/></> },
  'egg': { body: <path d="M12 3c-4 0-7 6-7 11a7 7 0 0 0 14 0c0-5-3-11-7-11z"/> },
  'fish': { body: <><path d="M16 8c-5-2-11 1-13 4 2 3 8 6 13 4 1-1 2-2 3-3-1-1-2-2-3-3z"/><path d="M19 12c1-1 2-2 2-2"/><path d="M19 12c1 1 2 2 2 2"/><circle cx="16" cy="11" r=".7" fill="currentColor"/></> },
  'meat': { body: <><path d="M7 5c-2 2-2 5 0 7 2 2 5 2 7 0l4 4-3 3-4-4c-2 2-5 2-7 0-2-2-2-5 0-7z"/><circle cx="9" cy="9" r=".7" fill="currentColor"/></> },
  'sparkles': { body: <><path d="m12 3 1.5 4.5L18 9l-4.5 1.5L12 15l-1.5-4.5L6 9l4.5-1.5z"/><path d="m18 16 .7 2 2 .7-2 .7-.7 2-.7-2-2-.7 2-.7z"/></> },
  'users': { body: <><circle cx="9" cy="8" r="3.5"/><path d="M3 20a6 6 0 0 1 12 0"/><path d="M16 5a3.5 3.5 0 0 1 0 7"/><path d="M18 14.5a6 6 0 0 1 3 5.5"/></> },
  'zap': { body: <path d="M13 3 4 14h7l-1 7 9-11h-7z"/> },

  // ── status ─────────────────────────────────────────────────
  'info': { body: <><circle cx="12" cy="12" r="9"/><path d="M12 11v5"/><path d="M12 8v.01"/></> },
  'warn': { body: <><path d="M12 4 21 20H3z"/><path d="M12 10v4"/><path d="M12 17v.01"/></> },
  'error': { body: <><circle cx="12" cy="12" r="9"/><path d="m9 9 6 6"/><path d="M15 9l-6 6"/></> },
  'success': { body: <><circle cx="12" cy="12" r="9"/><path d="m8 12 3 3 5-6"/></> },
  'loading': { body: <path d="M21 12a9 9 0 1 1-9-9"/> },

  // ── theme ──────────────────────────────────────────────────
  'sun': { body: <><circle cx="12" cy="12" r="4"/><path d="M12 3v2M12 19v2M3 12h2M19 12h2M5.6 5.6l1.4 1.4M17 17l1.4 1.4M5.6 18.4 7 17M17 7l1.4-1.4"/></> },
  'moon': { body: <path d="M20 14.5A8 8 0 0 1 9.5 4a8 8 0 1 0 10.5 10.5z"/> },

  // ── brand (multicolour, opt-in) ────────────────────────────

  // auth-provider — neutral placeholder. Google's four-colour G is a
  // trademarked asset and must be sourced from Google's official Identity
  // Branding Guidelines at integration. Drop the official SVG here at
  // integration time; until then the placeholder reserves the position.
  'auth-provider': {
    multi: true,
    body: <>
      <circle cx="12" cy="12" r="9" fill="none" stroke="var(--c-border-strong)" strokeWidth="1.5"/>
      <path d="M16 12.5h-4v-1.5h4" fill="none" stroke="var(--c-ink-2)" strokeWidth="1.5" strokeLinecap="round"/>
      <path d="M16 12.5a4 4 0 1 1-1.2-2.9" fill="none" stroke="var(--c-ink-2)" strokeWidth="1.5" strokeLinecap="round"/>
    </>,
  },
};

export const Icon = ({
  name,
  size = 18,
  fill,
  strokeWidth = 1.75,
  ...rest
}: IconProps) => {
  const def = ICONS[name];
  if (!def) return null;

  const common = {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    'aria-hidden': true,
    ...rest,
  };

  if (def.multi) {
    return <svg {...common}>{def.body}</svg>;
  }

  if (def.filled) {
    return (
      <svg {...common} fill={fill ?? 'currentColor'} stroke="none">
        {def.body}
      </svg>
    );
  }

  return (
    <svg
      {...common}
      fill={fill ?? 'none'}
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      {def.body}
    </svg>
  );
};
